<template>
  <div>
    <v-list-item :ref="moduleBulb.itemId" link @click="loadBulb()" :disabled="isLocked || bulbDeleted">
      <v-list-item-avatar class="mx-0">
        <v-icon class="grey--text" v-if="isLocked">mdi-lock</v-icon>
        <v-icon class="success--text" v-else-if="moduleItemAttemptCompleted">mdi-check-circle</v-icon>
        <v-icon :style="isActiveVideo ? activeVideoStyle : inactiveVideoStyle" v-else>mdi-play-circle</v-icon>
      </v-list-item-avatar>
      <v-list-item-content>
          <v-list-item-subtitle>{{ bulbName }}</v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
  </div>
</template>

<script>
import {
  mapActions,
  mapState,
  mapGetters,
  mapMutations,
} from 'vuex';
import _ from 'lodash';

export default {
  props: ['moduleBulb', 'moduleId'],
  data: () => ({
    inactiveVideoStyle: {
      color: 'gray',
    },
    shownNextItemToast: false,
  }),
  computed: {
    ...mapState([
      'activeModuleItemId',
      'activePlaylistVideo',
      'allModuleItemAttempts',
      'courseHash',
      'playlist',
      'playlistId',
      'nextItem',
      'requiredItemIndex',
      'sectionId',
      'user',
    ]),
    ...mapGetters([
      'snackbar',
    ]),
    activeVideoStyle() {
      return { color: this.playlist.default_color };
    },
    bulbDeleted() {
      return !_.get(this.moduleBulb, 'bulb');
    },
    bulbIndex() {
      return _.findIndex(this.playlist.allModuleItems, { itemId: this.moduleBulb.itemId });
    },
    bulbName() {
      return _.get(this.moduleBulb, 'bulb.name', 'Bulb Deleted');
    },
    isActiveVideo() {
      if (this.bulbDeleted) return false;
      return this.activeModuleItemId === this.moduleBulb.itemId;
    },
    bulbCompleted() {
      return (_.get(this.moduleBulb, 'bulb') ? _.get(this.moduleBulb, 'bulbAttempt.complete', null) === 1 : true);
    },
    isLocked() {
      if (
        _.get(this.playlist, 'config.lockProgression')
        && (
          // If a bulb was completed outside the playlist and lockedProgression is true - treat completed bulbs furthur in the playlist as locked
          !this.bulbCompleted
          || this.bulbIndex > this.requiredItemIndex
        )
        && this.bulbIndex !== this.requiredItemIndex
        && this.user.userType !== 'teacher'
        && this.requiredItemIndex < this.bulbIndex
      ) {
        return true;
      }
      return false;
    },
    hasScoreThresholdToAdd() {
      return (
        !this.moduleBulb.config?.clearedThresholdScore
        && (this.moduleBulb.config?.scoreThreshold ?? this.moduleBulb.bulb.config?.scoreThreshold)
      );
    },
    moduleItemAttempt() {
      return _.find(this.allModuleItemAttempts, { moduleItemId: this.moduleBulb.id });
    },
    moduleItemAttemptCompleted() {
      return _.get(this.moduleItemAttempt, 'complete', null) === 1;
    },
  },
  methods: {
    ...mapActions([
      'getOrCreateLMB',
      'markModuleItemAttemptComplete',
      'setModuleItem',
    ]),
    ...mapMutations([
      'forceCloseSnackbar',
      'setActiveLMB',
      'setActiveModuleItemId',
      'setActivePlaylistVideo',
      'setNextItemTimeoutToast',
      'toggleSnackbar',
    ]),
    getPlaylistBulbScoreThreshold() {
      return this.moduleBulb.config?.scoreThreshold ?? this.moduleBulb.bulb.config?.scoreThreshold;
    },
    async loadBulb() {
      this.setNextItemTimeoutToast(null);
      this.setModuleItem(this.moduleBulb);
      let lmbBulbId;
      if (this.moduleBulb?.bulb?.lmbId === 1) {
        let teacherToken;
        if (this.user.userType !== 'teacher') {
          // eslint-disable-next-line
          ({ data: { lmbBulbId, teacherToken } } = await this.getOrCreateLMB({
            bulbId: this.moduleBulb.itemId,
            sectionId: this.sectionId,
          }));
        } else lmbBulbId = this.moduleBulb.bulb.id;
        this.setActiveLMB({
          bulbId: lmbBulbId,
          name: this.moduleBulb.bulb.name,
          moduleId: this.moduleId,
          config: _.get(this.moduleBulb, 'config', {}),
          teacherToken,
        });
      } else {
        this.setActivePlaylistVideo({
          id: this.moduleBulb.bulb.id,
          name: this.moduleBulb.bulb.name,
          ...(this.hasScoreThresholdToAdd && { scoreThreshold: this.getPlaylistBulbScoreThreshold() }),
          moduleId: this.moduleId,
          config: _.get(this.moduleBulb, 'config', {}),
        });
      }
      const path = this.moduleBulb?.bulb?.lmbId === 1
        ? `/course/${this.courseHash}/module/${this.moduleId}/item/${lmbBulbId}`
        : `/course/${this.courseHash}/module/${this.moduleId}/item/${this.activePlaylistVideo.bulbId}`;
      if (this.$route.path !== path) {
        this.$router.push({
          name: 'Course Home',
          params: { courseHash: this.courseHash, moduleId: this.moduleId, itemId: this.activePlaylistVideo.bulbId || lmbBulbId },
        });
      }
      if (
        _.get(this.snackbar, 'open')
        && (
          this.moduleBulb.itemId !== this.activeModuleItemId
          && _.get(this.playlist, 'config.lockProgression')
        )
      ) this.forceCloseSnackbar();
    },
    popNextItemToast(completedOutOfPlaylist = false) {
      if (_.get(this.user, 'userType', '') === 'teacher') return;
      this.shownNextItemToast = true;
      const lastModuleItemIndex = this.playlist.allModuleItems.length - 1;
      const currentModuleItemIndex = _.findIndex(this.playlist.allModuleItems, { itemId: this.activeModuleItemId });
      if (currentModuleItemIndex === lastModuleItemIndex) {
        this.snackbarAction = false;
      } else {
        this.snackbarAction = true;
      }
      this.toggleSnackbar({
        text: completedOutOfPlaylist ? 'Bulb completed outside playlist.' : 'Bulb completed.',
        color: 'success',
        action: this.snackbarAction,
        timeout: this.snackbarAction ? -1 : 5000,
      });
    },
  },
  watch: {
    isActiveVideo(n) {
      if (n) this.$refs[this.activeModuleItemId].$el.focus();
      // Bulb is complete and user didn't click Next Item toast in lockedProgression playlist - re-pop toast
      if (
        n
        && this.bulbCompleted
        && this.requiredItemIndex === this.bulbIndex
        && _.get(this.playlist, 'config.lockProgression')
      ) this.popNextItemToast();
    },
    moduleItemAttempt() {
      /*
      * If the bulb has been completed outside of the playlist mark the moduleItemAttempt complete.  The playlist will
      * automatically jump to the next module item.
      */
      if (
        this.moduleItemAttempt
        && this.bulbCompleted
        && !_.get(this.moduleItemAttempt, 'complete')
      ) {
        this.popNextItemToast(true);
        this.markModuleItemAttemptComplete(this.moduleBulb.id);
      }
    },
  },
  mounted() {
    /*
    * This is to handle for a strange edge case where the bulb is complete and a moduleItemAttempt has been created,
    * but not completed. When mounted we will recognize this situation and pop the nextItem toast as well as mark the
    * moduleItemAttempt complete so on refresh or next load Playlist will load the correct moduleItem
    */
    if (
      _.get(this.moduleBulb, 'bulb')
      && _.get(this.moduleBulb, 'bulbAttempt.complete', null) === 1
      && _.get(this, 'moduleItemAttempt.complete') === 0
    ) {
      this.popNextItemToast();
      this.markModuleItemAttemptComplete(this.moduleBulb.id);
    }
  },
};
</script>

<style lang="scss">
  .v-list-item__subtitle {
    /* Overrides Vuetify styling on li elements to wrap longer bulb names */
    overflow: visible !important;
    white-space: normal !important;
  }
</style>
